// 008 — Emergencia (Fase 3, ver PLAN.md "FASE 3 — spec completa"). Nuevo
// desde cero — antes corría 100% en Patrón A (Questionnaire genérico
// `008.json`, 9 campos, compartido con 002/003 vía `extraerConsultaExterna`).
//
// **Tanda 17 de 2**: bloques B/C/D/E/F/G (`bloques008IngresoDatos.ts`/
// `Bloques008Ingreso.tsx`), entregados sin conectar.
// **Tanda 18 de 2 (cierra 008)**: bloques H/I/J/K/N/O/P
// (`bloques008ClinicoDatos.ts`/`Bloques008Clinico.tsx`) + este componente +
// alta en `FORMULARIOS_DISCRETOS` + retiro de 008 de Patrón A
// (`extractors.ts`/`medplum-admin.ts`).
//
// L (diagnósticos presuntivos) y M (diagnósticos definitivos) — hasta 3
// cada uno, según la fuente oficial — viven acá (no en los archivos de
// bloques) porque M crea `Condition` y ese flujo necesita `useMedplum`.
// Solo M crea `Condition`: un diagnóstico presuntivo en emergencia es
// explícitamente provisional (el propio formulario los separa), mismo
// criterio de "no crear recurso sin certeza clínica" ya aplicado en el
// resto del proyecto — L queda como `Par[]` puro. Se crean en una sola
// transacción (todo o nada), mismo patrón que 018A/019/038.
import { useMedplum } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import type { FormularioDiscretoProps, Par } from './index';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { LoteTransaccional } from './transaccionFhir';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';
import { SelectorDiagnosticoCie10, codeDeDiagnostico, type DiagnosticoCie10Valor } from './SelectorDiagnosticoCie10';
import { conCodigoCie } from './camposSimplesDatos';
import { Bloques008Ingreso } from './Bloques008Ingreso';
import { estadoInicialBloques008Ingreso, paresDeBloques008Ingreso } from './bloques008IngresoDatos';
import { alergiasFhirDe008, motivoDe008, observacionesDe008 } from './recursosFhir008';
import { Bloques008Clinico } from './Bloques008Clinico';
import { estadoInicialBloques008Clinico, paresDeBloques008Clinico } from './bloques008ClinicoDatos';

const CODIGO = '008';

const DIAGNOSTICO_VACIO: DiagnosticoCie10Valor = { texto: '', cie10: null };
const TOPE_DIAGNOSTICOS = 3;

function diagnosticosVacios(): DiagnosticoCie10Valor[] {
  return Array.from({ length: TOPE_DIAGNOSTICOS }, () => ({ ...DIAGNOSTICO_VACIO }));
}

export function Formulario008({ subject, encounter, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const { guardando, error, guardar } = useGuardado();

  const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
  const titulo = t('formularios.f008.titulo');
  // Servicio del encabezado del PDF (no el 'Consulta externa' por defecto): Registro de admisión de Emergencia: el papel no trae servicio; no es consulta externa.
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, CODIGO, titulo, { servicio: 'Emergencia' });

  const [ingreso, setIngreso] = useState(() => estadoInicialBloques008Ingreso());
  const [clinico, setClinico] = useState(() => estadoInicialBloques008Clinico());
  const [presuntivos, setPresuntivos] = useState<DiagnosticoCie10Valor[]>(() => diagnosticosVacios());
  const [definitivos, setDefinitivos] = useState<DiagnosticoCie10Valor[]>(() => diagnosticosVacios());

  function actualizarDiagnostico(lista: DiagnosticoCie10Valor[], setLista: (v: DiagnosticoCie10Valor[]) => void, indice: number, valor: DiagnosticoCie10Valor): void {
    setLista(lista.map((d, i) => (i === indice ? valor : d)));
  }

  const handleSubmit = (): Promise<void> =>
    guardar(async () => {
      const pares: Par[] = [...paresDeBloques008Ingreso(t, ingreso), ...paresDeBloques008Clinico(t, clinico)];
      const lote = new LoteTransaccional();
      const ahora = new Date().toISOString();

      // Mismo patrón que 002/003: motivo → Encounter.reasonCode, alergias → AllergyIntolerance, constantes → Observation.
      const motivo = motivoDe008(ingreso);
      if (motivo && encounter) {
        const actual = await medplum.readReference(encounter);
        lote.actualizarEncounterAbierto({ ...actual, id: actual.id!, reasonCode: [{ text: motivo }] });
      }
      for (const alergia of alergiasFhirDe008(ingreso, subject, ahora)) lote.crear(alergia);
      for (const obs of observacionesDe008(ingreso, subject, encounter, ahora)) lote.crear(obs);

      presuntivos.forEach((d, i) => {
        if (d.texto.trim()) pares.push({ pregunta: `${t('formularios.f008.bloqueL.titulo')} #${i + 1}`, respuesta: conCodigoCie(d) });
      });

      for (const d of definitivos) {
        if (!d.texto.trim()) continue;
        lote.crear({ resourceType: 'Condition', subject, encounter, code: codeDeDiagnostico(d), recordedDate: ahora });
      }
      definitivos.forEach((d, i) => {
        if (d.texto.trim()) pares.push({ pregunta: `${t('formularios.f008.bloqueM.titulo')} #${i + 1}`, respuesta: conCodigoCie(d) });
      });

      await lote.enviar(medplum);
      onGuardado(pares, datosEncabezadoMsp);
    });

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-base font-medium text-hce-ink">{titulo}</h3>

      <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

      <Bloques008Ingreso estado={ingreso} onChange={setIngreso} />

      <Bloques008Clinico estado={clinico} onChange={setClinico} />

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f008.bloqueL.titulo')}</h4>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {presuntivos.map((d, i) => (
            <SelectorDiagnosticoCie10
              key={String(i)}
              label={`${t('formularios.f008.bloqueL.titulo')} #${i + 1}`}
              value={d}
              onChange={(v) => actualizarDiagnostico(presuntivos, setPresuntivos, i, v)}
            />
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f008.bloqueM.titulo')}</h4>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {definitivos.map((d, i) => (
            <SelectorDiagnosticoCie10
              key={String(i)}
              label={`${t('formularios.f008.bloqueM.titulo')} #${i + 1}`}
              value={d}
              onChange={(v) => actualizarDiagnostico(definitivos, setDefinitivos, i, v)}
            />
          ))}
        </div>
      </div>

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
